import React, { useEffect, useMemo, useState } from 'react';

import { Web } from '@mui/icons-material';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import SalesTaxTable, { salesTaxColumns } from './SalesTaxTable';
import Excel from '../../../assets/excel.png';
import { Breadcrumbs, Button, EdgeReportingWindow, SubHeader, ReportLoader } from '../../../components';
import { newDateFormatted } from '../../../utils';
import ImageColorChanger from '../../../utils/imageColorChanger';
import getCSVData from '../csv';
import { getList, userHasAccess } from '../operations';
import SalesTaxReportForm from './SalesTaxReportForm';
import getEdgeReportingMenu from '../getEdgeReportingMenu';

import './styles.scoped.css';

const SalesTax = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { menuItems } = getEdgeReportingMenu(t);
  const { user } = useSelector(state => state.admin);
  const [data, setData] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);
  const [form, setForm] = useState({});
  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isLoadingTaxCodes, setIsLoadingTaxCodes] = useState(false);

  useEffect(() => {
    if (menuItems) {
      // eslint-disable-next-line no-undef
      if (!userHasAccess(user, menuItems.find(i => i.to === window.location.pathname)?.id)) navigate('/');
    }
  }, [menuItems]);

  const handleGetTaxCodes = async () => {
    setIsLoadingTaxCodes(true);
    const tc = await getList('tax-code');
    setTaxCodes(tc);
    setIsLoadingTaxCodes(false);
  };

  useEffect(() => {
    handleGetTaxCodes();
  }, []);

  const taxCodeOptions = useMemo(
    () =>
      taxCodes?.map(({ TaxCode, TaxCodeDescription }) => ({
        label: TaxCodeDescription,
        value: TaxCode,
      })),
    [taxCodes],
  );

  const fileName = `edge-reporting-sales-tax-${newDateFormatted.toString()}.csv`;

  return (
    <>
      <SubHeader
        text="EDGE Reporting"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle="Need help with your Sales Tax report?"
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
        }}>
        <Breadcrumbs
          leftTitle="Reporting Home"
          middleTitle={showResults && 'Sales Tax Report'}
          midItemAction={() => setShowResults(false)}
          rightTitle={showResults ? 'Viewing Report' : 'Sales Tax Report'}
          route="/edge-reporting"
        />
        {!!data?.length && showResults && (
          <CSVLink filename={fileName} data={getCSVData(data, salesTaxColumns)}>
            <ImageColorChanger
              src={Excel}
              style={{ padding: '20px 30px 0 0 ', cursor: 'pointer' }}
              alt="export-to-excel"
            />
          </CSVLink>
        )}
      </div>
      {loadingResults && <ReportLoader didAbort={() => setLoadingResults(false)} />}
      {showResults ? (
        <>
          <SalesTaxTable data={data} loading={loadingResults} />
          <div className="buttons-row">
            <Button variant="primary" onPress={() => setShowResults(false)} style={{ marginRight: '20px', width: '100px' }} text="Back" />

            <Button
              style={{ width: '100px' }}
              onPress={() => navigate('/edge-reporting')}
              variant="outline"
              text="Cancel"
            />
          </div>
        </>
      ) : (
        <EdgeReportingWindow
          containerStyle={{
            border: '2px solid #343a40',
          }}
          windowContent={
            <SalesTaxReportForm
              form={form}
              setForm={setForm}
              isLoadingTaxCodes={isLoadingTaxCodes}
              taxCodeOptions={taxCodeOptions}
              setLoadingResults={setLoadingResults}
              setData={setData}
              setShowResults={setShowResults}
            />
          }
          windowTitle="Criteria"
          headerStyle={{ backgroundColor: '#343a40' }}
        />
      )}
    </>
  );
};

export default SalesTax;
